// Everything a player does, in one bar under the circle: their own role, the night prompt when
// their character wakes, their hand during a vote, and the button that pings a Storyteller.
//
// Nothing here decides anything — every button is an emit, and the server is free to refuse it.

import { useState } from "react";
import { Check, Hand, HelpCircle } from "lucide-react";
import type { GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn, findCharacter, teamColor, teamLabel } from "../../lib/ui.js";
import { Button } from "../ui/primitives.js";

const HELP_RESET_MS = 45_000;

function NightPrompt({ view }: { view: GameView }) {
  const send = useGame((s) => s.send);
  const [picks, setPicks] = useState<string[]>([]);
  const [changing, setChanging] = useState(false);
  const entry = view.you.nightEntry;
  if (!entry) return null;

  const character = findCharacter(view, entry.characterId);
  const specs = character?.nightAction?.picks ?? [];
  if (specs.length === 0) {
    if (!entry.open) return null;
    return (
      <div className="text-sm text-parchment-dim">
        {character?.name}: the Storyteller will visit you — nothing to submit.
      </div>
    );
  }

  // Only the fact of submitting is shown, never an outcome: a drunk or poisoned pick looks the same.
  if (entry.submittedAt !== null && !changing) {
    const nameOf = (id: string): string =>
      view.seats.find((s) => s.id === id)?.displayName ?? findCharacter(view, id)?.name ?? "?";
    return (
      <div className="flex items-center gap-2">
        <Check className="size-5 text-brass" />
        <div>
          <div className="text-sm text-parchment">
            Submitted: {entry.picks.length > 0 ? entry.picks.map(nameOf).join(", ") : "no choice"}
          </div>
          <div className="text-xs text-parchment-dim">
            {entry.open ? "Waiting for the Storyteller." : "The Storyteller has your choice."}
          </div>
        </div>
        {entry.open && (
          <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>
            Change
          </Button>
        )}
      </div>
    );
  }

  if (!entry.open) {
    return <div className="text-sm text-parchment-dim">The Storyteller has moved on.</div>;
  }

  const ready = specs.every((spec, i) => spec.optional || picks[i]);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-brass">{entry.prompt}</span>
      {specs.map((spec, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1">
          <span className="mr-1 text-xs text-parchment-dim">{spec.label ?? `Choice ${i + 1}`}</span>
          {spec.type === "player"
            ? view.seats
                .filter((seat) => !(spec.excludeSelf && seat.id === view.you.seatId))
                .map((seat) => (
                  <Button
                    key={seat.id}
                    size="sm"
                    variant={picks[i] === seat.id ? "primary" : "secondary"}
                    onClick={() =>
                      setPicks((prev) => {
                        const next = [...prev];
                        next[i] = seat.id;
                        return next;
                      })
                    }
                  >
                    {seat.displayName}
                  </Button>
                ))
            : view.script.characters.map((c) => (
                <Button
                  key={c.id}
                  size="sm"
                  variant={picks[i] === c.id ? "primary" : "secondary"}
                  onClick={() =>
                    setPicks((prev) => {
                      const next = [...prev];
                      next[i] = c.id;
                      return next;
                    })
                  }
                >
                  {c.name}
                </Button>
              ))}
          {i === specs.length - 1 && (
            <Button
              variant="primary"
              size="sm"
              className="ml-auto"
              disabled={!ready}
              onClick={async () => {
                // Skipped optional picks leave gaps; send only the chosen ones.
                const chosen = picks.filter((id): id is string => Boolean(id));
                const ack = await send("player:nightPick", { entryId: entry.id, picks: chosen });
                if (ack.ok) {
                  setPicks([]);
                  setChanging(false);
                }
              }}
            >
              Submit
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

export function PlayerBar({ view }: { view: GameView }) {
  const send = useGame((s) => s.send);
  const [helpSentAt, setHelpSentAt] = useState<number | null>(null);

  const mySeat = view.seats.find((s) => s.id === view.you.seatId) ?? null;
  const myCharacter = findCharacter(view, view.you.characterId);
  const openNomination = view.nominations.find((n) => n.id === view.openNominationId) ?? null;
  const voting = openNomination?.status === "voting";
  const myHand = mySeat ? Boolean(openNomination?.hands[mySeat.id]) : false;

  // The button re-enables the instant the ping is dismissed; this timeout is only a backstop for
  // when that update never arrives, so it can never sit stuck on "sent".
  const helpPending =
    view.you.helpRequestPending || (helpSentAt !== null && Date.now() - helpSentAt < HELP_RESET_MS);

  const exile = Boolean(openNomination?.exile);
  // Exiles don't use ghost votes.
  const canVote = Boolean(mySeat && (mySeat.alive || exile || !mySeat.ghostVoteUsed));

  return (
    <div className="pointer-events-auto flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-ink-line bg-ink-soft/95 px-3 py-2 backdrop-blur">
      <div className="flex items-center gap-2">
        {myCharacter?.icon && <img src={myCharacter.icon} alt="" className="size-11 object-contain" />}
        <div>
          <div className="text-base font-medium text-parchment">
            {myCharacter?.name ?? (view.rolesRevealed ? "No character" : "Roles not out yet")}
          </div>
          {myCharacter && (
            <div className="text-xs" style={{ color: teamColor(myCharacter.team, mySeat?.alignmentOverride) }}>
              {teamLabel(myCharacter.team, mySeat?.alignmentOverride)}
            </div>
          )}
        </div>
      </div>

      {myCharacter?.ability && (
        <p className="max-w-sm text-xs leading-snug text-parchment-dim">{myCharacter.ability}</p>
      )}

      <div className="h-8 w-px bg-ink-line" />

      {view.phase.kind === "night" && <NightPrompt view={view} />}

      {voting && openNomination && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-parchment-dim">
            {exile ? "Vote to exile" : "Vote on"}{" "}
            {view.seats.find((s) => s.id === openNomination.nomineeSeatId)?.displayName}
          </span>
          <Button
            variant={myHand ? "primary" : "secondary"}
            disabled={!canVote}
            onClick={() => void send("player:vote", { nominationId: openNomination.id, raised: !myHand })}
            title={canVote ? undefined : "Your ghost vote is spent"}
          >
            <Hand className={cn("size-3.5", myHand && "text-brass")} />
            {myHand ? "Hand up" : "Raise hand"}
          </Button>
          {mySeat && !mySeat.alive && !exile && !mySeat.ghostVoteUsed && (
            <span className="text-xs text-brass">this spends your ghost vote</span>
          )}
        </div>
      )}

      {view.daySubPhase === "nominations" && view.phase.kind === "day" && !openNomination && (
        <span className="text-sm text-parchment-dim">
          Nominations are open — click a seat to nominate.
        </span>
      )}

      <div className="ml-auto">
        <Button
          variant={helpPending ? "secondary" : "primary"}
          disabled={helpPending}
          onClick={async () => {
            const ack = await send("player:requestHelp", {});
            if (ack.ok) setHelpSentAt(Date.now());
          }}
          title="Ping the Storyteller"
        >
          <HelpCircle className="size-3.5" />
          {helpPending ? "Storyteller pinged" : "Need the Storyteller"}
        </Button>
      </div>
    </div>
  );
}
